'use client'

/**
 * VerifyDisplay REPL — typewriter "verify_this (λ)" inside a REPL-style window
 * with results panel (All paths explored, etc.). Saved for later use; not rendered in the hero.
 */

// React
import { useState, useEffect } from 'react'

// Animation
import { motion, AnimatePresence } from 'framer-motion'

const VERIFY_TEXT = 'verify_this'
const VERIFY_SUFFIX = '(λ)'
const RESULTS = [
  '✓  All paths explored',
  '✓  No counterexamples',
  '✓  Formally proven',
]

export function VerifyDisplayRepl() {
  const [phase, setPhase] = useState<'cursor' | 'typing' | 'result'>('cursor')
  const [typed, setTyped] = useState('')
  const fullText = VERIFY_TEXT + VERIFY_SUFFIX

  useEffect(() => {
    const t = setTimeout(() => setPhase('typing'), 1000)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    if (phase !== 'typing') return
    if (typed.length < fullText.length) {
      const t = setTimeout(
        () => setTyped(fullText.slice(0, typed.length + 1)),
        70 + Math.random() * 50,
      )
      return () => clearTimeout(t)
    } else {
      const t = setTimeout(() => setPhase('result'), 500)
      return () => clearTimeout(t)
    }
  }, [phase, typed, fullText])

  const showCursor = phase === 'cursor' || phase === 'typing'

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="hidden lg:flex lg:items-center lg:justify-center"
    >
      <div className="w-full max-w-sm rounded-2xl border border-slate-700/50 bg-slate-900/80 shadow-2xl backdrop-blur-md">
        {/* Window chrome */}
        <div className="flex items-center gap-1.5 border-b border-slate-700/40 px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
          <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/60" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-500/60" />
          <span className="ml-2 font-mono text-[11px] text-slate-500">
            codelogician ~ repl
          </span>
        </div>

        {/* REPL body */}
        <div className="p-6">
          {/* Input line */}
          <div className="flex items-baseline gap-2 font-mono text-2xl">
            <span className="text-teal-400 select-none">›</span>
            <span className="text-white">
              {typed.slice(0, VERIFY_TEXT.length)}
              <span className="text-teal-300">
                {typed.slice(VERIFY_TEXT.length)}
              </span>
            </span>
            {showCursor && (
              <span className="ml-px inline-block h-[1.15em] w-[3px] animate-pulse rounded-sm bg-teal-400 align-middle" />
            )}
          </div>

          {/* Results */}
          <AnimatePresence>
            {phase === 'result' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-5 space-y-2 border-t border-slate-700/40 pt-5"
              >
                {RESULTS.map((line, i) => (
                  <motion.p
                    key={line}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.18 }}
                    className="font-mono text-sm text-teal-300"
                  >
                    {line}
                  </motion.p>
                ))}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: RESULTS.length * 0.18 + 0.1 }}
                  className="mt-3 flex items-baseline gap-2 font-mono text-2xl"
                >
                  <span className="text-teal-400 select-none">›</span>
                  <span className="inline-block h-[1.15em] w-[3px] animate-pulse rounded-sm bg-teal-400 align-middle" />
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}
